import {ALayer, buckets } from "./internalModules";
import { Dist } from "./Dist";
import Graphics, { updateCameraToScene } from "./Graphics";
import { mapMerge, mapMultiply, mapSum } from "./utility";
import {WallType} from "./internalModules";


export class WallTypeRibbon extends WallType{

  layersGlazed: ALayer[]
  glazedHeight: Dist //the height of window
  solidHeight: Dist //the height of each solid wall portion above and below ribbon window
  wallTypeGlazed: WallType //the wall type used for ribbon window
  wallTypeSolid: WallType //the wall type used for solid wall portions
  solidSubTypes: WallType[] //the parts of solid wall around window

  constructor (height: Dist, width: Dist, windowToWallRatio: number) {
    super(height, width)
    this.className = 'WallTypeRibbon'
    this.glazedHeight = new Dist( windowToWallRatio * height.toFeet(), 0)
    this.solidHeight = new Dist( (height.toFeet() - windowToWallRatio * height.toFeet()) /2 ,0) //to be set with wwr
    this.layersGlazed = []
    this.wallTypeGlazed = new WallType(this.glazedHeight, this.width)
    this.wallTypeSolid = new WallType(this.solidHeight, this.width)
    this.solidSubTypes = []
    this._wwr = windowToWallRatio
  }

  set wwr(newValue: number) {
    let newWWR = newValue
    if (newValue > 0.95) {
      newWWR = 0.95
    }
    if (newValue < 0.05) {
      newWWR = 0.05
    }
    this._wwr = newWWR
    this.updateHeight(this.height)
    this.updateWidth(this.width)
  }

  get wwr() {
    return this._wwr
  }

  /**method to add a layer*/
  addLayer(bucket: string, layer: ALayer) {
    if(bucket === buckets.wall){
      this.layers.push(layer)
    }
    if(bucket === buckets.glazed){
      this.layersGlazed.push(layer)
    }
    this.regenSubtypes()
    this.calculateGwpSF()
    this.regenGeometry()
  }

  /**returns the array of layers of the specified bucket */
  getLayers(bucket: string): ALayer[] {
    let layers: ALayer[] = []
    switch (bucket) {
      case buckets.wall:
        layers = this.layers
        break
      case buckets.glazed:
        layers = this.layersGlazed
        break
      default:
        break
    }
    return layers
  }

  /**returns all layers */
  getAllLayers(): ALayer[] {
    return this.getLayers(buckets.wall).concat(this.getLayers(buckets.glazed))
  }
  
  /**update a layer in the specified bucket */
  updateLayer(bucket: string, layer: ALayer, index: number){
    switch (bucket) {
      case buckets.wall:
        this.layers.splice(index, 1, layer)
        break
      case buckets.glazed:
        this.layersGlazed.splice(index, 1, layer)
        break
      default:
        break
    }
    this.regenSubtypes()
    this.calculateGwpSF()
    this.regenGeometry()
  }

  /** remove a layer from this wall assembly*/
  removeLayer(bucket: string, index: number){
    if(bucket === buckets.wall){
      this.layers.splice(index, 1)
    }
    if(bucket === buckets.glazed){
      this.layersGlazed.splice(index, 1)
    }
    this.regenSubtypes()
    this.calculateGwpSF()
    this.regenGeometry()
  }

  /** reorder a solid layer in this wall assembly*/
  reorderLayer(bucket: string, fromIndex: number, toIndex: number) {
    let layers = this.layers
    if(bucket === buckets.glazed){
      layers = this.layersGlazed
    }
    let layer = layers[fromIndex]
    layers.splice(fromIndex, 1)
    layers.splice(toIndex, 0, layer)
    this.regenSubtypes()
    this.regenGeometry()
  }

  //method to update module height
  updateHeight(ht: Dist) {
    this.height = ht
    this.glazedHeight = new Dist( this.wwr * this.height.toFeet(), 0)
    this.solidHeight = new Dist( (this.height.toFeet() - this.glazedHeight.toFeet()) / 2, 0)
    this.regenSubtypes()
    this.calculateGwpSF()
    this.regenGeometry()
  }

  //method to update module width
  updateWidth(wd: Dist) {
    this.width = wd
    this.regenSubtypes()
    this.calculateGwpSF()
    this.regenGeometry()
  }

  //refresh sub walltypes
  regenSubtypes() {
    //update glazed type
    this.wallTypeGlazed.height = this.glazedHeight
    this.wallTypeGlazed.width = this.width
    this.wallTypeGlazed.exploded = this.exploded
    this.wallTypeGlazed.layers = []
    for (const layer of this.layersGlazed) {
      this.wallTypeGlazed.addLayer(buckets.wall, layer)
    }
    //update solid type
    this.wallTypeSolid.height = this.solidHeight
    this.wallTypeSolid.width = this.width
    this.wallTypeSolid.exploded = this.exploded
    this.wallTypeSolid.layers = []
    for (const layer of this.layers) {
      this.wallTypeSolid.addLayer(buckets.wall, layer)
    }
    //regenerate solid subtypes
    this.solidSubTypes = []
    let subtype = this.wallTypeSolid.getCopy()
    // make solid top part
    subtype.height = Dist.feet( (this.height.ft - this.glazedHeight.ft) / 2 )
    subtype.width = this.width
    this.solidSubTypes.push(subtype)
    // make solid bottom part, duplicate of top part
    this.solidSubTypes.push(subtype)
  }

  calculateGwpSF(){
    this.gwpsfByMaterial.clear()
    //get glazed portion gwp
    for (const layer of this.layersGlazed) {
      mapMerge(this.gwpsfByMaterial, layer.getMaterialGwpMap(this.wallTypeGlazed))
    }
    //get solid portions gwp
    for (const subtype of this.solidSubTypes) {
      for (const layer of subtype.layers) {
        mapMerge(this.gwpsfByMaterial, layer.getMaterialGwpMap(subtype))
      }
    }
    //store total gwp per sf from all materials
    this.gwpsf = mapSum(this.gwpsfByMaterial) / this.area
    //divide each gwp in the map by the area
    mapMultiply(this.gwpsfByMaterial, 1 / this.area)
    //console.log(`gwpsf: ${this.gwpsf}`)
    //console.log(this.gwpsfByMaterial)
  }

  regenGeometry() {
    Graphics.clearSceneMeshes(this.scene)
    if(this.layerCount < 1){
      return
    }
    // geometry for upper solid subtype
    let upper = this.wallTypeSolid.getMesh(0,this._explodeAmount)[0]
    upper.translateY( this.wallTypeGlazed.height.ft / 2 + this.wallTypeSolid.height.ft / 2)
    this.scene.add(upper)

    // geometry for lower solid subtype
    let [lower, startingOffset] = this.wallTypeSolid.getMesh(0, this._explodeAmount)
    lower.translateY( -this.wallTypeGlazed.height.ft / 2 - this.wallTypeSolid.height.ft / 2)
    this.scene.add(lower)

    // geometry for glazed subtype
    if (!this._exploded){
      startingOffset = 0
    }
    this.scene.add(this.wallTypeGlazed.getMesh(startingOffset, this._explodeAmount)[0])
    this.colorMeshes()
    updateCameraToScene(this.camera, this.scene, this.orbitControls)
  }

  //custom method to convert to json
  toJSON(): object {
    return {
      className: this.className,
      guid: this.guid,
      name: this.name,
      height: this.height.ft.toString(),
      width: this.width.ft.toString(),
      length: '0',
      layers: this.layers,
      layersGlazed: this.layersGlazed,
      layersFraming: [],
      layersColumn: [],
      wwr: this._wwr.toString()
    }
  }
}